"use client";

import React from "react";
import Link from "next/link";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shadcn/ui/form";
import { Button } from "@/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/ui/dialog";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  email: z.string().email({ message: "Please enter a valid email" }),
  subject: z.string().min(1, { message: "Subject is required" }),
  message: z.string().min(1, { message: "Message is required" }),
});

type FormValues = z.infer<typeof formSchema>;

const fieldClass = cn(
  "flex w-full px-[1.75rem] py-[1.5rem] rounded-[.75rem]",
  "bg-white border border-black/10 text-[1.75rem] text-[#103037]",
  "placeholder:text-black/40 placeholder:font-normal",
  "transition-colors focus:outline-none focus:border-[#103037]/40",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export default function LegalContactForm() {
  const [loading, setLoading] = React.useState(false);
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [submittedFirstName, setSubmittedFirstName] = React.useState<string>("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  async function onSubmit(values: FormValues) {
    setLoading(true);

    // Fake network request — replace with a real endpoint when ready.
    await new Promise((resolve) => setTimeout(resolve, 1200));

    setSubmittedFirstName(values.firstName);
    setLoading(false);
    setSuccessOpen(true);
    form.reset();
  }

  return (
    <div className="mx-auto w-full py-[4rem] lg:py-[6rem]">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col w-full gap-[2rem]"
          noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[2rem]">
            <FormField
              control={form.control}
              name="firstName"
              disabled={loading}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-[1.5rem] text-[#103037]">First name</FormLabel>
                  <FormControl>
                    <input
                      type="text"
                      placeholder="Jane"
                      autoComplete="given-name"
                      className={fieldClass}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-[1.25rem]" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              disabled={loading}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-[1.5rem] text-[#103037]">Last name</FormLabel>
                  <FormControl>
                    <input
                      type="text"
                      placeholder="Doe"
                      autoComplete="family-name"
                      className={fieldClass}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-[1.25rem]" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium text-[1.5rem] text-[#103037]">Email</FormLabel>
                <FormControl>
                  <input
                    type="email"
                    placeholder="jane@example.com"
                    autoComplete="email"
                    className={fieldClass}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-[1.25rem]" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="subject"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium text-[1.5rem] text-[#103037]">Subject</FormLabel>
                <FormControl>
                  <input
                    type="text"
                    placeholder="How can we help?"
                    className={fieldClass}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-[1.25rem]" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-medium text-[1.5rem] text-[#103037]">Message</FormLabel>
                <FormControl>
                  <textarea
                    placeholder="Write your message..."
                    rows={6}
                    className={cn(fieldClass, "min-h-[14rem] resize-y leading-[1.5]")}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-[1.25rem]" />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={loading}
            variant="default"
            className="relative w-full mt-[1rem] overflow-clip">
            <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg" />
            <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover" />
            <div className="relative leading-none overflow-clip">
              <div className="transition-transform duration-500 group-hover:-translate-y-full button-text">
                {loading ? "Sending..." : "Send message"}
              </div>
              <div className="absolute top-0 left-0 transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-text-hover">
                {loading ? "Sending..." : "Send message"}
              </div>
            </div>
          </Button>

          <p className="text-center text-[1.25rem] text-black/60 leading-[1.6]">
            By submitting this form you agree to our{" "}
            <Link
              href="/beyond-terms-and-conditions"
              className="underline transition-colors hover:text-[#103037]">
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link
              href="/beyond-privacy"
              className="underline transition-colors hover:text-[#103037]">
              Privacy Policy
            </Link>
            .
          </p>

        </form>
      </Form>

      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="max-w-[95vw] md:max-w-[72rem] max-h-[90vh] !border-none !bg-white !p-0 rounded-[1.75rem] lg:rounded-[2.75rem] overflow-clip">
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle>Message sent</DialogTitle>
              <DialogDescription>
                Your message has been sent successfully.
              </DialogDescription>
            </DialogHeader>
          </VisuallyHidden>

          <div className="flex flex-col">
            {/* Hero band — matches FlashSaleDialog's top image area */}
            <div className="relative w-full h-[18rem] lg:h-[22rem] bg-gradient-to-br from-[#E6F2EB] to-[#A3DEB2] flex items-center justify-center overflow-clip">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.6),transparent_60%)]" />
              <div className="relative flex items-center justify-center w-[9rem] h-[9rem] lg:w-[11rem] lg:h-[11rem] rounded-full bg-[#103037]">
                <svg
                  className="w-[4rem] h-[4rem] lg:w-[5rem] lg:h-[5rem]"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M5 12.5L10 17.5L19 7.5"
                    stroke="#A3DEB2"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <div className="px-[3rem] sm:px-[5rem] lg:px-[10rem] pt-[4.15rem] lg:pt-[6rem] pb-[5rem] lg:pb-[7rem] w-full relative z-[1] flex flex-col">
              <div className="richtext richtext-center prose-headings:text-[#103037] prose-headings:text-[4rem] lg:prose-headings:text-[5rem] prose-headings:mb-[2rem] prose-headings:leading-[1.05] prose-p:text-[2rem] prose-p:text-[#103037]/80 prose-strong:font-normal prose-strong:text-[#103037]">
                <h2>
                  Message sent
                  {submittedFirstName ? `, ${submittedFirstName}` : ""}
                </h2>
                <p>
                  Thanks for reaching out — we&apos;ve received your message
                  and will get back to you within{" "}
                  <strong>1&ndash;2 business days</strong>.
                </p>
              </div>

              <div className="flex relative z-10 justify-center mt-[3.5rem]">
                <Button
                  type="button"
                  variant="default"
                  onClick={() => setSuccessOpen(false)}
                  className="relative w-full sm:w-auto sm:min-w-[25.75rem] overflow-clip"
                >
                  <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg" />
                  <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover" />
                  <div className="relative leading-none overflow-clip">
                    <div className="transition-transform duration-500 group-hover:-translate-y-full button-text">
                      Close
                    </div>
                    <div className="absolute top-0 left-0 transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-text-hover">
                      Close
                    </div>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
